import {
  Injectable,
  Injector,
  ComponentRef,
  Optional,
  SkipSelf,
} from '@angular/core';
import {
  ComponentType,
  Overlay,
  OverlayConfig,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NavigationStart, Router } from '@angular/router';
import { Observable, filter, takeUntil, Subject } from 'rxjs';
import { DialogContainerComponent } from './dialog-container.component';
import { DialogRef } from './dialog-ref';
import { DialogConfig, DIALOG_DATA } from './dialog.types';

let uniqueId = 0;

/**
 * Service for opening dialogs. Provided by DialogModule (not root) so that
 * each lazy-loaded module gets an instance with the correct injector hierarchy
 * — matching how Angular Material's MatDialog worked.
 */
@Injectable()
export class DialogService {
  /**
   * Currently open dialog references across all DialogService instances.
   * Uses `any` because DialogRef<T, R> is not covariant due to Subject's
   * contravariant Observer type — DialogRef<T, R> is not assignable to
   * DialogRef<unknown, unknown>.
   */
  get openDialogs(): DialogRef<any, any>[] {
    return this._parentDialog
      ? this._parentDialog.openDialogs
      : this._ownOpenDialogs;
  }

  private readonly _ownOpenDialogs: DialogRef<any, any>[] = [];
  private readonly afterAllClosedSubject = new Subject<void>();

  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector,
    private readonly router: Router,
    @Optional() @SkipSelf() private readonly _parentDialog: DialogService | null
  ) {}

  /**
   * Open a component inside a dialog overlay.
   *
   * @param component The component class to render.
   * @param config Optional dialog configuration.
   * @returns A DialogRef for controlling the dialog lifecycle.
   */
  open<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: DialogConfig<D>
  ): DialogRef<T, R> {
    const resolvedConfig = this.applyDefaults(config);
    const overlayRef = this.createOverlay(resolvedConfig);

    let containerRef: ComponentRef<DialogContainerComponent>;
    let dialogRef: DialogRef<T, R>;

    try {
      containerRef = this.attachContainer(overlayRef, resolvedConfig);
      dialogRef = this.createDialogRef<T, R>(
        overlayRef,
        containerRef,
        resolvedConfig
      );

      this.attachComponent<T, D, R>(
        component,
        containerRef,
        dialogRef,
        resolvedConfig
      );

      containerRef.instance._initFocusTrap();
    } catch (error) {
      overlayRef.dispose();
      throw error;
    }

    this.registerDialog(dialogRef, resolvedConfig, overlayRef);

    return dialogRef;
  }

  /** Emits when all open dialogs have been closed. */
  afterAllClosed(): Observable<void> {
    return this.afterAllClosedSubject.asObservable();
  }

  /** Close all currently open dialogs. */
  closeAll(): void {
    let i = this.openDialogs.length;
    while (i--) {
      this.openDialogs[i].close();
    }
  }

  /**
   * Get a dialog by its ID.
   */
  getDialogById(id: string): DialogRef<any, any> | undefined {
    return this.openDialogs.find((d) => d.id === id);
  }

  private applyDefaults<D>(config?: DialogConfig<D>): DialogConfig<D> {
    return {
      role: 'dialog',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: 'first-tabbable',
      closeOnNavigation: true,
      ...config,
    };
  }

  private createOverlay(config: DialogConfig): OverlayRef {
    const positionStrategy = this.overlay.position().global();

    if (config.position?.left) {
      positionStrategy.left(config.position.left);
    } else if (config.position?.right) {
      positionStrategy.right(config.position.right);
    } else {
      positionStrategy.centerHorizontally();
    }

    if (config.position?.top) {
      positionStrategy.top(config.position.top);
    } else if (config.position?.bottom) {
      positionStrategy.bottom(config.position.bottom);
    } else {
      positionStrategy.centerVertically();
    }

    const overlayConfig = new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass || 'cdk-overlay-dark-backdrop',
      panelClass: config.panelClass,
      width: config.width,
      height: config.height,
      minWidth: config.minWidth,
      minHeight: config.minHeight,
      maxWidth: config.maxWidth ?? '80vw',
      maxHeight: config.maxHeight,
    });

    return this.overlay.create(overlayConfig);
  }

  private attachContainer(
    overlayRef: OverlayRef,
    config: DialogConfig
  ): ComponentRef<DialogContainerComponent> {
    const portal = new ComponentPortal(DialogContainerComponent);
    const containerRef = overlayRef.attach(portal);
    return containerRef;
  }

  private createDialogRef<T, R>(
    overlayRef: OverlayRef,
    containerRef: ComponentRef<DialogContainerComponent>,
    config: DialogConfig
  ): DialogRef<T, R> {
    const id = config.id || `app-dialog-${uniqueId++}`;
    const dialogRef = new DialogRef<T, R>(overlayRef, id, config.disableClose);

    containerRef.instance._init(dialogRef, config);

    return dialogRef;
  }

  private attachComponent<T, D, R>(
    component: ComponentType<T>,
    containerRef: ComponentRef<DialogContainerComponent>,
    dialogRef: DialogRef<T, R>,
    config: DialogConfig<D>
  ): void {
    const userInjector =
      config.injector || config.viewContainerRef?.injector || this.injector;

    const injector = Injector.create({
      parent: userInjector,
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: config.data ?? null },
      ],
    });

    const portal = new ComponentPortal(
      component,
      config.viewContainerRef || null,
      injector
    );
    const componentRef =
      containerRef.instance.portalOutlet.attachComponentPortal(portal);

    dialogRef.componentInstance = componentRef.instance;
  }

  private registerDialog(
    dialogRef: DialogRef<any, any>,
    config: DialogConfig,
    overlayRef: OverlayRef
  ): void {
    this.openDialogs.push(dialogRef);

    dialogRef.afterClosed().subscribe(() => {
      const index = this.openDialogs.indexOf(dialogRef);
      if (index > -1) {
        this.openDialogs.splice(index, 1);
      }
      if (this.openDialogs.length === 0) {
        this.afterAllClosedSubject.next();
      }
    });

    if (config.hasBackdrop && !config.disableClose) {
      overlayRef
        .backdropClick()
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(() => {
          if (!dialogRef.disableClose) {
            dialogRef.close();
          }
        });
    }

    overlayRef
      .keydownEvents()
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((event) => {
        if (event.key === 'Escape' && !dialogRef.disableClose) {
          event.preventDefault();
          dialogRef.close();
        }
      });

    if (config.closeOnNavigation) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationStart),
          takeUntil(dialogRef.afterClosed())
        )
        .subscribe(() => {
          dialogRef.close();
        });
    }
  }
}
